import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuoteStore } from '@/store/quoteStore';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, PoundSterling, Package } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PrioritySelector from '@/components/PrioritySelector';
import BlendAreaSummary from '@/components/steps/summary/BlendAreaSummary';

import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import logger from '@/utils/logger';
const API = `${API_URL}/api`;

const Step7Summary = ({ leadId, projectId, sourceQuoteId }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState(null);
  const [submittedId, setSubmittedId] = useState(null);
  const [submittedDraft, setSubmittedDraft] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);

  // "Order these materials" — turns the saved price into a confirmed Ox order.
  const handlePlaceOrder = async () => {
    setPlacingOrder(true);
    try {
      await axios.post(`${API}/quotes/${submittedId}/place-order`, {}, { withCredentials: true });
      setSubmittedDraft(false);
      toast.success(`Order ${submittedRef} placed with Ox Resin`);
    } catch (error) {
      toast.error(formatApiError(error?.response?.data) || 'Could not place the order');
    } finally { setPlacingOrder(false); }
  };
  const [submittedProjectId, setSubmittedProjectId] = useState(null);
  const [priority, setPriority] = useState('no_rush');
  const {
    measurementType,
    blendAreas,
    border,
    wastage,
    requiresMesh,
    meshRolls,
    ancillaries,
    customerDetails,
    getTotalM2,
    getWastagePercentage,
    getMeshRolls,
    setCurrentStep,
    reset
  } = useQuoteStore();

  const totalM2 = getTotalM2();
  const wastagePercentage = getWastagePercentage();
  const totalM2WithWastage = totalM2 * (1 + wastagePercentage);
  const actualMeshRolls = meshRolls || 0;

  // ── Live £ cost — pulled from the installer's Material Cost tier ────────
  // Fetches once on mount. If the installer isn't assigned a tier or Ox
  // haven't set prices yet, the section shows a friendly "Prices not
  // configured" hint instead of blowing up.
  const [pricing, setPricing] = useState(null);
  const [pricingLoaded, setPricingLoaded] = useState(false);
  const [vatInclusive, setVatInclusive] = useState(false);
  const [collectFromOx, setCollectFromOx] = useState(false); // if true, no pallet delivery line
  const VAT_RATE = 0.20;
  const PALLET_CHARGE = 75; // £ per pallet, ex-VAT — delivery estimation

  // ── Correct "mixes" totals for the calculation payload ──────────────────
  // NB: this MUST match the surface/base mix logic used in the on-screen
  // Calculation Summary (lines ~370-440 below). When measurementType is m²,
  // `getTotalQuantity`/`getTotalWithWastage` return raw m² values (a legacy
  // shortcut), so we can't use them for the mixes fields — that produced the
  // "38.5 m² AND 38.5 mixes" bug on the order detail page.
  // Coverage per mix (m²) — sourced from Staff-editable settings (fetched
  // together with tier prices below). Hardcoded fallbacks kick in only if
  // the /installer/probound-pricing feed is missing calc_settings, which
  // means we degrade gracefully during the brief prod deploy window.
  // IMPORTANT: this block must sit AFTER the `pricing` useState above,
  // otherwise reading `pricing?.calc_settings` here throws a temporal-dead-
  // zone ReferenceError and the review page renders blank.
  const DEFAULT_COVERAGE = {
    vehicle_surface_m2_per_mix: 3.5,
    vehicle_base_m2_per_mix:    3.2,
    foot_surface_m2_per_mix:    4.0,
    foot_base_m2_per_mix:       6.4,
  };
  const cov = (key) => (pricing?.calc_settings?.coverage?.[key] ?? DEFAULT_COVERAGE[key]);
  const surfaceCoveragePerMix = (isVehicle) => cov(isVehicle ? 'vehicle_surface_m2_per_mix' : 'foot_surface_m2_per_mix');
  const baseCoveragePerMix    = (isVehicle) => cov(isVehicle ? 'vehicle_base_m2_per_mix'    : 'foot_base_m2_per_mix');

  const mixesForArea = (area, applyWastage) => {
    const q = parseFloat(area.quantity) || 0;
    if (measurementType === 'Mixes') {
      const surface = applyWastage ? Math.ceil(q * (1 + wastagePercentage)) : q;
      const baseRaw = parseFloat(area.base_mixes) || 0;
      const base = applyWastage ? Math.ceil(baseRaw * (1 + wastagePercentage)) : baseRaw;
      return surface + base;
    }
    // Square metres mode — derive mixes from m² using coverage per mix.
    const areaM2 = applyWastage ? q * (1 + wastagePercentage) : q;
    const spec = area.specification || '';
    const isVehicle = spec.includes('Vehicle');
    const hasBase = spec.includes('base');
    const surface = Math.ceil(areaM2 / surfaceCoveragePerMix(isVehicle));
    const base = hasBase ? Math.ceil(areaM2 / baseCoveragePerMix(isVehicle)) : 0;
    return surface + base;
  };
  const totalMixesBeforeWastage = blendAreas.reduce((n, a) => n + mixesForArea(a, false), 0);
  const totalMixesAfterWastage  = blendAreas.reduce((n, a) => n + mixesForArea(a, true),  0);

  const unit = measurementType === 'Square metres (m²)' ? 'm²' : 'mixes';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/installer/probound-pricing`, { credentials: 'include' });
        if (!cancelled) {
          if (r.ok) setPricing(await r.json());
          setPricingLoaded(true);
        }
      } catch (_) {
        if (!cancelled) setPricingLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const norm = (s = '') => (s + '').replace(/\*/g, '').toLowerCase().split(/\s+/).filter(Boolean).join(' ');

  // Look up a blend price: try the exact name, then "<name> OX Bespoke Blend"
  const blendPrice = (blendName) => {
    if (!pricing?.prices_by_name || !blendName) return null;
    const map = pricing.prices_by_name;
    return map[norm(blendName)]
      ?? map[norm(`${blendName} OX Bespoke Blend`)]
      ?? null;
  };

  // Estimated pallets required — used both by the Materials Cost card (to
  // add a £75/pallet delivery charge line) and the Delivery Estimation card.
  // Same formula as the original inline calc: ceil((mixes × 4) / 40) + 1.
  const estimatedPallets = useMemo(() => {
    const wpc = getWastagePercentage();
    let totalMixes = 0;
    for (const area of blendAreas) {
      const q = parseFloat(area.quantity) || 0;
      if (!q) continue;
      if (measurementType === 'Mixes') {
        totalMixes += Math.ceil(q * (1 + wpc));
        const b = parseFloat(area.base_mixes) || 0;
        if (b > 0) totalMixes += Math.ceil(b * (1 + wpc));
      } else {
        const spec = area.specification || '';
        const isVehicle = spec.includes('Vehicle');
        const hasBase = spec.includes('base');
        const areaM2 = q * (1 + wpc);
        totalMixes += Math.ceil(areaM2 / surfaceCoveragePerMix(isVehicle));
        if (hasBase) totalMixes += Math.ceil(areaM2 / baseCoveragePerMix(isVehicle));
      }
    }
    totalMixes += (border?.mixes || 0);
    if (totalMixes <= 0) return 0;
    return Math.ceil((totalMixes * 4) / 40) + 1;
  }, [blendAreas, measurementType, border]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute cost per blend area using the mix logic we already trust.
  // Splits Surface and Base into their own line items because they map to
  // different products on the pricelist (bespoke blend vs base build-up).
  const costLines = useMemo(() => {
    const lines = [];
    // Resolve the base build-up unit price. Current product name is the
    // short brand-forward "OxResin ProBound Base". Older names kept as one-hop
    // fallbacks so quotes don't lose their price in the brief window between
    // frontend deploy and the DB-side rename migration running on prod.
    const baseUnitPrice = pricing?.prices_by_name?.[norm('OxResin ProBound Base')]
      ?? pricing?.prices_by_name?.[norm('Ox Resin Bound Base Build Up 3.2m2 @ 30mm')]
      ?? pricing?.prices_by_name?.[norm('Ox Resin Bound Base Build Up 1.75m2 @ 30-35mm')]
      ?? null;
    for (const area of blendAreas) {
      const q = parseFloat(area.quantity) || 0;
      if (!q) continue;
      let surfaceMixes = 0;
      let baseMixes = 0;
      if (measurementType === 'Mixes') {
        surfaceMixes = Math.ceil(q * (1 + wastagePercentage));
        const b = parseFloat(area.base_mixes) || 0;
        if (b > 0) baseMixes = Math.ceil(b * (1 + wastagePercentage));
      } else {
        const spec = area.specification || '';
        const isVehicle = spec.includes('Vehicle');
        const hasBase = spec.includes('base');
        const areaM2 = q * (1 + wastagePercentage);
        surfaceMixes = Math.ceil(areaM2 / surfaceCoveragePerMix(isVehicle));
        if (hasBase) baseMixes = Math.ceil(areaM2 / baseCoveragePerMix(isVehicle));
      }
      if (surfaceMixes > 0) {
        const unitPrice = blendPrice(area.surface_blend);
        lines.push({
          label: area.surface_blend || 'Surface blend',
          qty: surfaceMixes,
          unitLabel: 'mixes',
          unitPrice,
          lineTotal: unitPrice != null ? unitPrice * surfaceMixes : null,
        });
      }
      if (baseMixes > 0) {
        lines.push({
          label: 'Base build-up',
          qty: baseMixes,
          unitLabel: 'mixes',
          unitPrice: baseUnitPrice,
          lineTotal: baseUnitPrice != null ? baseUnitPrice * baseMixes : null,
        });
      }
    }
    return lines;
  }, [blendAreas, measurementType, wastagePercentage, pricing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ancillary line items — mesh rolls, primer / lubricant / glass / IPA, machinery
  const ancillaryLines = useMemo(() => {
    const lines = [];
    const priceFor = (...names) => {
      const map = pricing?.prices_by_name || {};
      for (const n of names) {
        const v = map[norm(n)];
        if (v != null) return v;
      }
      return null;
    };
    if (requiresMesh && meshRolls > 0) {
      const meshPrice = priceFor(
        'Ox Resin Bound Sub Base Mesh (150sqm Roll)',
        'Resin Bound Membrane 100m2 Roll',
      );
      lines.push({
        label: 'Reinforcement mesh',
        qty: meshRolls,
        unitLabel: 'roll',
        unitPrice: meshPrice,
        lineTotal: meshPrice != null ? meshPrice * meshRolls : null,
      });
    }
    // Additional items typed on Step 5 (ancillaries store)
    const anc = ancillaries || {};
    const addLine = (qtyKey, label, ...names) => {
      const qty = parseFloat(anc[qtyKey]) || 0;
      if (qty <= 0) return;
      const unitPrice = priceFor(...names);
      lines.push({
        label,
        qty,
        unitLabel: 'unit',
        unitPrice,
        lineTotal: unitPrice != null ? unitPrice * qty : null,
      });
    };
    addLine('primer', '5L Driveway Primer', 'Resin Bound Primer 5 Litre Tin');
    addLine('trowel_lubricant', 'Trowel Lubricant (5L)', '5 Litre Trowel Lubricant', '5 Litre Trowel Cleaner');
    addLine('crushed_glass', 'Crushed Glass (5kg)', 'Anti-Slip Crushed Glass Grit');
    addLine('isopropanol', 'Isopropanol (5L)', 'Isopropanol 5 Litre', '5 Litre Isopropanol');
    // Quattromix is now just another ancillary picked on Step 5, so it flows
    // through the same addLine path — no separate checkbox needed.
    addLine(
      'quattromix',
      '120L Quattromix Forced Action Mixer',
      '120L Quattromix Forced Action Mixer',
      'QUATTROMIX 4-BAG 1201 Resin Bound Mixer Forced Action Mixer',
    );

    // Delivery charge — £75/pallet ex-VAT. Only added if we have pallets to
    // move AND the installer hasn't ticked "Collect from Ox".
    if (estimatedPallets > 0 && !collectFromOx) {
      lines.push({
        label: `Delivery (${estimatedPallets} pallet${estimatedPallets === 1 ? '' : 's'} × £${PALLET_CHARGE})`,
        qty: estimatedPallets,
        unitLabel: 'pallet',
        unitPrice: PALLET_CHARGE,
        lineTotal: PALLET_CHARGE * estimatedPallets,
      });
    }

    return lines;
  }, [requiresMesh, meshRolls, pricing, ancillaries, estimatedPallets, collectFromOx]);

  const priceableLines = [...costLines, ...ancillaryLines].filter((l) => l.lineTotal != null);
  const missingPriceLines = [...costLines, ...ancillaryLines].filter((l) => l.lineTotal == null);
  const subtotalExVat = priceableLines.reduce((s, l) => s + l.lineTotal, 0);
  const vatAmount = subtotalExVat * VAT_RATE;
  const totalIncVat = subtotalExVat + vatAmount;

  const fmt = (n) => `£${(Number.isFinite(n) ? n : 0).toFixed(2)}`;


  const handleBack = () => {
    setCurrentStep(6);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const quoteData = {
        measurement_type: measurementType,
        blend_areas: blendAreas.map(area => ({
          surface_blend: area.surface_blend,
          quantity: parseFloat(area.quantity),
          specification: area.specification
        })),
        border: border ? {
          mode: border.mode,
          blend_color: border.blend_color,
          mixes: border.mixes,
          rigid_trim: border.rigid_trim,
          formable_trim: border.formable_trim,
          length: border.length,
          width: border.width
        } : null,
        wastage: wastage,
        requires_mesh: requiresMesh,
        customer_details: {
          name: customerDetails.name,
          email: customerDetails.email,
          phone: customerDetails.phone,
          address: customerDetails.address || undefined,
          company: customerDetails.company || undefined
        },
        calculation: {
          total_quantity_before_wastage: totalMixesBeforeWastage,
          total_quantity_after_wastage: totalMixesAfterWastage,
          total_m2: totalM2WithWastage,
          mesh_rolls_required: requiresMesh ? actualMeshRolls : 0,
          wastage_percentage: Math.round(wastagePercentage * 100)
        },
        ancillaries: {
          trowel_lubricant: ancillaries.trowel_lubricant || 0,
          primer: ancillaries.primer || 0,
          crushed_glass: ancillaries.crushed_glass || 0,
          isopropanol: ancillaries.isopropanol || 0
        },
        // Freeze the exact £ breakdown the installer saw at submit time.
        // Staff will use this for the Xero draft invoice — never re-price
        // against a moving tier after submission.
        pricing_snapshot: {
          lines: priceableLines.map((l) => ({
            label: l.label,
            qty: Number(l.qty) || 0,
            unit_label: l.unitLabel || null,
            unit_price: Number(l.unitPrice) || 0,
            line_total: Number(l.lineTotal) || 0,
          })),
          missing_price_labels: missingPriceLines.map((l) => l.label),
          subtotal_ex_vat: Number(subtotalExVat.toFixed(2)),
          vat_rate: VAT_RATE,
          vat_amount: Number(vatAmount.toFixed(2)),
          total_inc_vat: Number(totalIncVat.toFixed(2)),
          currency: 'GBP',
          tier_name: pricing?.tier?.label || pricing?.tier_name || null,
        },
        lead_id: leadId || null,
        project_id: projectId || null,
        source_quote_id: sourceQuoteId || null,
        as_draft: !!sourceQuoteId, // from a customer quote: save a PRICE, order later via "Order these materials"
        priority: priority,
        // Fulfilment: 'delivery' adds £75/pallet to Materials Cost; 'collect'
        // means installer picks up from Ox, so no delivery line is added.
        fulfilment: collectFromOx ? 'collect' : 'delivery'
      };

      const response = await axios.post(`${API}/quotes`, quoteData, { withCredentials: true });
      
      const ref = response.data.reference;
      const projId = response.data.project_id;
      setSubmittedRef(ref);
      setSubmittedProjectId(projId);
      setSubmittedId(response.data.id);
      setSubmittedDraft(response.data.status === 'draft');

      toast.success(sourceQuoteId ? `Material price ${ref} saved to your customer quote` : `Material quote ${ref} submitted!`);
    } catch (error) {
      logger.error('Error submitting quote:', error);
      const detail =
        formatApiError(error?.response?.data) ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to submit quote. Please try again.';
      // Pydantic returns an array of validation errors — collapse to a readable string
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).join('.')} — ${d.msg}`).join('  ·  ')
        : String(detail);
      toast.error(`Failed to submit quote: ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewQuote = () => {
    reset();
    setSubmittedRef(null);
    setSubmittedProjectId(null);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-2xl md:text-3xl font-bold text-[#27292E] mb-2">
        Quote Summary
      </h2>
      <p className="text-gray-600 mb-8">
        Please review your quote details before submitting.
      </p>

      <div className="space-y-6">
        {/* Customer Details */}
        <div className="border-2 border-gray-200 rounded-xl p-5">
          <h3 className="text-lg font-bold text-[#27292E] mb-4">Customer Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Name:</span>
              <span className="font-medium text-[#27292E]">{customerDetails.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="font-medium text-[#27292E]">{customerDetails.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Phone:</span>
              <span className="font-medium text-[#27292E]">{customerDetails.phone}</span>
            </div>
            {customerDetails.company && (
              <div className="flex justify-between">
                <span className="text-gray-600">Company:</span>
                <span className="font-medium text-[#27292E]">{customerDetails.company}</span>
              </div>
            )}
            {customerDetails.address && (
              <div className="flex justify-between">
                <span className="text-gray-600">Address:</span>
                <span className="font-medium text-[#27292E]">{customerDetails.address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Project Details */}
        <div className="border-2 border-gray-200 rounded-xl p-5">
          <h3 className="text-lg font-bold text-[#27292E] mb-4">Project Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Measurement Type:</span>
              <span className="font-medium text-[#27292E]">{measurementType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Number of Areas:</span>
              <span className="font-medium text-[#27292E]">
                {blendAreas.length}
              </span>
            </div>
          </div>
        </div>

        {/* Blend Areas */}
        {blendAreas.map((area, index) => (
          <BlendAreaSummary
            key={`area-${area.specification || ''}-${area.quantity || ''}-${index}`}
            area={area}
            index={index}
            totalAreas={blendAreas.length}
            measurementType={measurementType}
            getWastagePercentage={getWastagePercentage}
            coverage={pricing?.calc_settings?.coverage}
          />
        ))}

        {/* Border Details */}
        {border && border.blend_color && (
          <div className="border-2 border-[#300122] rounded-xl p-5 bg-[#300122]/5" data-testid="border-summary">
            <h3 className="text-lg font-bold text-[#27292E] mb-4">Resin Border</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Blend Colour:</span>
                <span className="font-medium text-[#27292E]">{border.blend_color}</span>
              </div>
              {border.mode === 'calculate' && border.length && border.width && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Border Dimensions:</span>
                    <span className="font-medium text-[#27292E]">
                      {border.length}m x {border.width}mm
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Border Area:</span>
                    <span className="font-medium text-[#27292E]">
                      {(border.length * border.width / 1000).toFixed(3)} m²
                    </span>
                  </div>
                </>
              )}
              <div className="pt-2 border-t-2 border-gray-300">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-700 font-bold">Border Mixes:</span>
                  <span className="font-bold text-[#300122] text-lg">{border.mixes} mixes</span>
                </div>
              </div>
              {border.rigid_trim > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Border Rigid Trim:</span>
                  <span className="font-medium text-[#27292E]">
                    {border.rigid_trim} lengths ({(border.rigid_trim * 2.5).toFixed(1)}m)
                  </span>
                </div>
              )}
              {border.formable_trim > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Border Formable Trim:</span>
                  <span className="font-medium text-[#27292E]">
                    {border.formable_trim} lengths ({(border.formable_trim * 2.5).toFixed(1)}m)
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Additional Items (Ancillaries) - above Total Trim */}
        {(ancillaries.trowel_lubricant > 0 || ancillaries.primer > 0 || ancillaries.crushed_glass > 0 || ancillaries.isopropanol > 0 || ancillaries.rigid_trim > 0 || ancillaries.formable_trim > 0) && (
          <div className="border-2 border-gray-200 rounded-xl p-5">
            <h3 className="text-lg font-bold text-[#27292E] mb-4">Additional Items</h3>
            <div className="space-y-2 text-sm">
              {ancillaries.trowel_lubricant > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Trowel Lubricant/Cleaner (5l):</span>
                  <span className="font-medium text-[#27292E]">{ancillaries.trowel_lubricant}</span>
                </div>
              )}
              {ancillaries.primer > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">5l Driveway Primer:</span>
                  <span className="font-medium text-[#27292E]">{ancillaries.primer}</span>
                </div>
              )}
              {ancillaries.crushed_glass > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Crushed glass (5kg):</span>
                  <span className="font-medium text-[#27292E]">{ancillaries.crushed_glass}</span>
                </div>
              )}
              {ancillaries.isopropanol > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Isopropanol (5l):</span>
                  <span className="font-medium text-[#27292E]">{ancillaries.isopropanol}</span>
                </div>
              )}
              {ancillaries.rigid_trim > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">18mm Rigid Trim (additional):</span>
                  <span className="font-medium text-[#27292E]">{ancillaries.rigid_trim} lengths</span>
                </div>
              )}
              {ancillaries.formable_trim > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">18mm Formable Trim (additional):</span>
                  <span className="font-medium text-[#27292E]">{ancillaries.formable_trim} lengths</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Total Trim Summary */}
        {((border?.rigid_trim > 0 || border?.formable_trim > 0) || 
          (ancillaries.rigid_trim > 0 || ancillaries.formable_trim > 0)) && (
          <div className="border-2 border-[#2F5E96] rounded-xl p-5 bg-[#2F5E96]/5" data-testid="trim-summary">
            <h3 className="text-lg font-bold text-[#27292E] mb-4">Total Trim Required</h3>
            <div className="space-y-3 text-sm">
              {/* Rigid Trim */}
              {((border?.rigid_trim || 0) + (ancillaries.rigid_trim || 0)) > 0 && (
                <div>
                  <div className="flex justify-between font-bold text-[#2F5E96]">
                    <span>18mm Rigid Trim Total:</span>
                    <span>{(border?.rigid_trim || 0) + (ancillaries.rigid_trim || 0)} lengths</span>
                  </div>
                  {(border?.rigid_trim > 0 && ancillaries.rigid_trim > 0) && (
                    <div className="text-xs text-gray-500 mt-1 pl-2">
                      (Border: {border.rigid_trim} + Additional: {ancillaries.rigid_trim})
                    </div>
                  )}
                </div>
              )}
              {/* Formable Trim */}
              {((border?.formable_trim || 0) + (ancillaries.formable_trim || 0)) > 0 && (
                <div>
                  <div className="flex justify-between font-bold text-[#2F5E96]">
                    <span>18mm Formable Trim Total:</span>
                    <span>{(border?.formable_trim || 0) + (ancillaries.formable_trim || 0)} lengths</span>
                  </div>
                  {(border?.formable_trim > 0 && ancillaries.formable_trim > 0) && (
                    <div className="text-xs text-gray-500 mt-1 pl-2">
                      (Border: {border.formable_trim} + Additional: {ancillaries.formable_trim})
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 italic mt-2 pt-2 border-t border-gray-200">
                Note: Trim comes in packs of 5 or 10, so your trim order will be rounded to the nearest full pack.
              </p>
            </div>
          </div>
        )}

        {/* Calculation Summary */}
        <div className="border-2 border-[#300122] rounded-xl p-5 bg-[#300122]/5">
          <h3 className="text-lg font-bold text-[#27292E] mb-4">Calculation Summary</h3>
          <div className="space-y-3 text-sm">
            {measurementType === 'Square metres (m²)' && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Area (before wastage):</span>
                  <span className="font-bold text-[#27292E]">
                    {totalM2.toFixed(2)} m²
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Wastage:</span>
                  <span className="font-medium text-[#27292E]">{wastage}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Area (with wastage):</span>
                  <span className="font-bold text-[#2F5E96]">
                    {totalM2WithWastage.toFixed(2)} m²
                  </span>
                </div>
              </>
            )}
            
            {measurementType === 'Mixes' && (
              <div className="flex justify-between">
                <span className="text-gray-600">Wastage:</span>
                <span className="font-medium text-[#27292E]">{wastage}</span>
              </div>
            )}
            
            {/* Total Mixes Breakdown */}
            <div className="pt-3 border-t-2 border-gray-300">
              <div className="flex justify-between mb-2">
                <span className="text-gray-700 font-bold">Total Surface Mixes:</span>
                <span className="font-bold text-[#2F5E96] text-lg">
                  {blendAreas.reduce((sum, area) => {
                    const quantity = parseFloat(area.quantity) || 0;
                    
                    if (measurementType === 'Mixes') {
                      // Direct mixes entry with wastage
                      const wastagePercentage = getWastagePercentage();
                      return sum + Math.ceil(quantity * (1 + wastagePercentage));
                    } else {
                      // Calculate from m²
                      const areaM2 = quantity;
                      const wastagePercentage = getWastagePercentage();
                      const areaWithWastage = areaM2 * (1 + wastagePercentage);
                      let surfaceMixes;
                      if (area.specification?.includes('Vehicle')) {
                        surfaceMixes = Math.ceil(areaWithWastage / surfaceCoveragePerMix(true));
                      } else {
                        surfaceMixes = Math.ceil(areaWithWastage / surfaceCoveragePerMix(false));
                      }
                      return sum + surfaceMixes;
                    }
                  }, 0)} mixes
                </span>
              </div>
              
              {(() => {
                // Check if there are any base mixes
                const hasBaseMixes = blendAreas.some(area => {
                  if (measurementType === 'Mixes') {
                    return parseFloat(area.base_mixes) > 0;
                  } else {
                    return area.specification?.includes('base');
                  }
                });
                
                if (!hasBaseMixes) return null;
                
                return (
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-bold">Total Base Mixes:</span>
                    <span className="font-bold text-[#2F5E96] text-lg">
                      {blendAreas.reduce((sum, area) => {
                        if (measurementType === 'Mixes') {
                          // Direct base mixes entry with wastage
                          const baseMixes = parseFloat(area.base_mixes) || 0;
                          if (baseMixes === 0) return sum;
                          const wastagePercentage = getWastagePercentage();
                          return sum + Math.ceil(baseMixes * (1 + wastagePercentage));
                        } else {
                          // Calculate from m²
                          if (!area.specification?.includes('base')) return sum;
                          const quantity = parseFloat(area.quantity) || 0;
                          const areaM2 = quantity;
                          const wastagePercentage = getWastagePercentage();
                          const areaWithWastage = areaM2 * (1 + wastagePercentage);
                          let baseMixes;
                          if (area.specification.includes('Vehicle')) {
                            baseMixes = Math.ceil(areaWithWastage / baseCoveragePerMix(true));
                          } else {
                            baseMixes = Math.ceil(areaWithWastage / baseCoveragePerMix(false));
                          }
                          return sum + baseMixes;
                        }
                      }, 0)} mixes
                    </span>
                  </div>
                );
              })()}
            </div>
            
            <div className="flex justify-between pt-3 border-t-2 border-gray-200">
              <span className="text-gray-600">Reinforcement Mesh:</span>
              <span className="font-bold text-[#27292E]">
                {requiresMesh ? `Yes - ${actualMeshRolls} roll${actualMeshRolls > 1 ? 's' : ''}` : 'No'}
              </span>
            </div>
          </div>
        </div>

        {/* Delivery Estimation */}
        <div className="border-2 border-[#2F5E96] rounded-xl p-5 bg-[#2F5E96]/5">
          <h3 className="text-lg font-bold text-[#27292E] mb-4">Delivery Estimation</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Estimated Pallets:</span>
              <span className="font-bold text-[#2F5E96] text-xl">
                {estimatedPallets} pallet{estimatedPallets === 1 ? '' : 's'}
              </span>
            </div>

            {/* Delivery vs Collect toggle — zeroes out the £75/pallet line. */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t border-[#2F5E96]/20">
              <span className="text-gray-600">Fulfilment:</span>
              <div
                className="inline-flex rounded-full bg-white border border-[#2F5E96]/30 p-0.5 self-start sm:self-auto"
                data-testid="delivery-toggle"
              >
                <button
                  type="button"
                  onClick={() => setCollectFromOx(false)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${!collectFromOx ? 'bg-[#2F5E96] text-white' : 'text-[#2F5E96] hover:bg-[#2F5E96]/5'}`}
                  data-testid="delivery-toggle-deliver"
                >
                  Deliver (£{PALLET_CHARGE}/pallet)
                </button>
                <button
                  type="button"
                  onClick={() => setCollectFromOx(true)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${collectFromOx ? 'bg-[#2F5E96] text-white' : 'text-[#2F5E96] hover:bg-[#2F5E96]/5'}`}
                  data-testid="delivery-toggle-collect"
                >
                  Collect from Ox
                </button>
              </div>
            </div>

            <div className="flex justify-between text-xs text-slate-500">
              <span>Delivery charge</span>
              <span>
                {collectFromOx
                  ? 'No charge — collecting from Ox'
                  : `£${(PALLET_CHARGE * estimatedPallets).toFixed(2)} ex-VAT · included in Materials Cost below`}
              </span>
            </div>
          </div>
        </div>

        {/* Materials Cost — live pricing from the installer's assigned tier */}
        {pricingLoaded && (
          <div className="border-2 border-emerald-700 rounded-xl p-5 bg-emerald-50/40">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h3 className="text-lg font-bold text-[#27292E] flex items-center gap-2">
                <PoundSterling className="w-5 h-5 text-emerald-700" /> Materials Cost
                {pricing?.tier?.label && (
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                    {pricing.tier.label} tier
                  </span>
                )}
              </h3>
              {/* VAT toggle */}
              <div className="inline-flex rounded-full bg-white border border-emerald-200 p-0.5 text-xs" data-testid="quote-vat-toggle">
                <button
                  type="button"
                  onClick={() => setVatInclusive(false)}
                  className={`px-3 py-1 rounded-full font-semibold ${!vatInclusive ? 'bg-emerald-600 text-white' : 'text-emerald-700'}`}
                >
                  Ex-VAT
                </button>
                <button
                  type="button"
                  onClick={() => setVatInclusive(true)}
                  className={`px-3 py-1 rounded-full font-semibold ${vatInclusive ? 'bg-emerald-600 text-white' : 'text-emerald-700'}`}
                >
                  Inc-VAT
                </button>
              </div>
            </div>

            {!pricing?.tier ? (
              <p className="text-sm text-slate-600 italic">
                Ox haven't set up your pricing tier yet — ask them to configure your Material Cost tier so quotes show live totals.
              </p>
            ) : priceableLines.length === 0 && missingPriceLines.length === 0 ? (
              <p className="text-sm text-slate-600 italic">Add a blend area to see the cost breakdown.</p>
            ) : (
              <>
                <div className="space-y-1.5 text-sm">
                  {priceableLines.map((l, i) => {
                    const shown = vatInclusive ? l.lineTotal * (1 + VAT_RATE) : l.lineTotal;
                    return (
                      <div key={`p-${i}`} className="flex justify-between">
                        <span className="text-slate-700">
                          {l.label} <span className="text-slate-400">· {l.qty} × {fmt(vatInclusive ? l.unitPrice * (1 + VAT_RATE) : l.unitPrice)}</span>
                        </span>
                        <span className="font-semibold text-slate-900">{fmt(shown)}</span>
                      </div>
                    );
                  })}
                  {missingPriceLines.map((l, i) => (
                    <div key={`m-${i}`} className="flex justify-between text-slate-500">
                      <span>{l.label} <span className="text-amber-600 text-xs italic">· price not set</span></span>
                      <span>—</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-emerald-200 mt-3 pt-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Subtotal (ex-VAT)</span>
                    <span className="font-semibold">{fmt(subtotalExVat)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">VAT (20%)</span>
                    <span className="font-semibold">{fmt(vatAmount)}</span>
                  </div>
                  <div className="flex justify-between text-base pt-1">
                    <span className="font-bold text-slate-900">Total (inc-VAT)</span>
                    <span className="font-bold text-emerald-700">{fmt(totalIncVat)}</span>
                  </div>
                </div>

                {missingPriceLines.length > 0 && (
                  <p className="text-[11px] text-amber-700 mt-2 italic">
                    Some items don't have a price on your tier — ask Ox to update Set Material Costs.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="mt-6">
        <PrioritySelector value={priority} onChange={setPriority} />
      </div>

      <div className="flex gap-4 mt-8">
        {submittedRef ? (
          <div className="w-full space-y-4">
            <div className={`border-2 rounded-xl p-6 text-center ${submittedDraft ? 'bg-sky-50 border-sky-200' : 'bg-green-50 border-green-200'}`} data-testid="submit-result-card">
              <div className={`font-bold text-lg mb-1 ${submittedDraft ? 'text-sky-700' : 'text-green-600'}`}>{submittedDraft ? 'Material price saved' : 'Quote Submitted'}</div>
              <div className="text-2xl font-bold text-[#27292E]">{submittedRef}</div>
              {submittedDraft ? (
                <>
                  <p className="text-sm text-gray-600 mt-2">This is a price only — nothing has been ordered yet. It&apos;s linked to your customer quote so you can see your margin.</p>
                  <Button onClick={handlePlaceOrder} disabled={placingOrder} className="mt-4 h-12 px-6 bg-[#2F5E96] hover:bg-[#254b7a] text-white font-bold rounded-xl" data-testid="place-order-btn">
                    {placingOrder ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Package className="w-5 h-5 mr-2" />} Order these materials
                  </Button>
                </>
              ) : submittedProjectId && (
                <p className="text-sm text-gray-500 mt-2">A project has been created for this order.</p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              {submittedProjectId && (
                <Button
                  onClick={() => { reset(); navigate(`/projects/${submittedProjectId}`); }}
                  className="flex-1 bg-[#2F5E96] hover:bg-[#254b7a] text-white h-12 sm:h-14 text-base sm:text-lg font-bold rounded-xl"
                  data-testid="view-project-btn"
                >
                  View Project
                </Button>
              )}
              <Button
                onClick={handleNewQuote}
                variant="outline"
                className="flex-1 h-12 sm:h-14 text-base sm:text-lg font-medium rounded-xl border-2 border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/5"
                data-testid="new-quote-btn"
              >
                New Quote
              </Button>
              <Button
                onClick={() => { reset(); navigate('/probound'); }}
                variant="outline"
                className="flex-1 h-12 sm:h-14 text-base sm:text-lg font-medium rounded-xl border-2 border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Back to Portal
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Button
              onClick={handleBack}
              disabled={isSubmitting}
              variant="outline"
              className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/5 disabled:opacity-50"
              data-testid="back-button-step7"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 bg-[#2F5E96] hover:bg-[#254b7a] text-white h-14 text-lg font-bold rounded-xl transition-transform active:scale-95 disabled:opacity-50"
              data-testid="submit-quote-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : sourceQuoteId ? (
                'Save material price'
              ) : (
                'Submit Quote'
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default Step7Summary;